import { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Download, Trash2 } from 'lucide-react';
import StatusBadge from './StatusBadge';
import ProviderBadge from './ProviderBadge';
import DateRangeSelector, { getFilterDates } from './DateRangeSelector';
import type { DateFilter } from './DateRangeSelector';
import { timeAgo } from './timeAgo';
import { downloadCsv } from './csvExport';
import { supabase } from '@shared/lib/supabase';
import type { Order, OrderStatus } from '@shared/types';

const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'authorized', label: 'Authorized' },
  { value: 'redirected', label: 'Redirected' },
  { value: 'checkout_opened', label: 'Checkout Opened' },
  { value: 'payment_initiated', label: 'Payment Initiated' },
  { value: 'usdc_received', label: 'USDC Received' },
  { value: 'forwarding', label: 'Forwarding' },
  { value: 'completed', label: 'Completed' },
  { value: 'refunded', label: 'Refunded' },
  { value: 'failed', label: 'Failed' },
  { value: 'expired', label: 'Abandoned' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'disputed', label: 'Disputed' },
];

const PAGE_SIZE = 25;

function isStripeOrder(o: Order) {
  return o.provider === 'stripe' || o.payment_provider === 'stripe';
}

function isSquareOrder(o: Order) {
  return o.provider === 'square' || o.payment_provider === 'square';
}

function isCryptoOrder(o: Order) {
  return o.provider === 'crypto' || o.payment_provider === 'crypto';
}

type SortField = 'created_at' | 'expected_amount' | 'status';
type SortDir = 'asc' | 'desc';

interface Props {
  solution?: 'crypto' | 'stripe' | 'square';
}

export default function Orders({ solution }: Props) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<DateFilter>({ range: '30d' });
  const [page, setPage] = useState(0);
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const label = solution === 'stripe'
    ? 'Stripe Orders'
    : solution === 'square'
      ? 'Square Orders'
      : solution === 'crypto'
        ? 'Crypto Orders'
        : 'Orders';
  const basePath = solution === 'stripe'
    ? '/dashboard/stripe/orders'
    : solution === 'square'
      ? '/dashboard/square/orders'
      : solution === 'crypto'
        ? '/dashboard/crypto/orders'
        : '/dashboard/orders';

  useEffect(() => {
    async function load() {
      const { start, end } = getFilterDates(dateFilter);
      let query = supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }
      if (start) query = query.gte('created_at', start);
      if (end) query = query.lte('created_at', end);

      const { data } = await query;
      if (data) {
        let result = data as unknown as Order[];
        if (solution === 'crypto') {
          result = result.filter(isCryptoOrder);
        } else if (solution === 'stripe') {
          result = result.filter(isStripeOrder);
        } else if (solution === 'square') {
          result = result.filter(isSquareOrder);
        }
        setOrders(result);
      }
      setLoading(false);
    }
    load();
  }, [statusFilter, solution, dateFilter]);

  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, dateFilter, sortField, sortDir]);

  const filtered = useMemo(() => {
    let result = orders;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (o) =>
          o.id.toLowerCase().includes(q) ||
          o.external_order_id?.toLowerCase().includes(q) ||
          o.deposit_address.toLowerCase().includes(q),
      );
    }
    result = [...result].sort((a, b) => {
      let cmp = 0;
      if (sortField === 'created_at') {
        cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      } else if (sortField === 'expected_amount') {
        cmp = a.expected_amount - b.expected_amount;
      } else if (sortField === 'status') {
        cmp = a.status.localeCompare(b.status);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return result;
  }, [orders, search, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageOrders = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const totalVolume = orders
    .filter((o) => o.status === 'completed')
    .reduce((sum, o) => sum + (o.verified_amount || o.expected_amount), 0);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortIndicator = (field: SortField) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' \u2191' : ' \u2193';
  };

  const handleExport = () => {
    const headers = ['ID', 'External ID', 'Amount', 'Provider', 'Status', 'Created'];
    const rows = filtered.map((o) => [
      o.id,
      o.external_order_id || '',
      o.expected_amount.toFixed(2),
      o.provider || o.payment_provider || '',
      o.status,
      new Date(o.created_at).toISOString(),
    ]);
    downloadCsv(headers, rows, `orders-${solution || 'all'}-${dateFilter.range}`);
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = pageOrders.every((o) => next.has(o.id));
      if (allSelected) {
        pageOrders.forEach((o) => next.delete(o.id));
      } else {
        pageOrders.forEach((o) => next.add(o.id));
      }
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0 || deleting) return;
    const count = selected.size;
    if (!window.confirm(`Delete ${count} order${count === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setDeleting(true);
    setDeleteError('');
    const ids = Array.from(selected);
    const { error } = await supabase.from('orders').delete().in('id', ids);
    if (error) {
      setDeleteError(error.message || 'Failed to delete orders');
      setDeleting(false);
      return;
    }
    setOrders((prev) => prev.filter((o) => !selected.has(o.id)));
    setSelected(new Set());
    setDeleting(false);
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>{label}</h1>
        <div className="flex items-center gap-3">
          {selected.size > 0 && (
            <button
              onClick={handleDeleteSelected}
              disabled={deleting}
              title={`Delete ${selected.size} selected`}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded font-mono text-xs transition-colors disabled:opacity-40"
              style={{ border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: '#ef4444' }}
            >
              <Trash2 size={13} />
              {deleting ? 'Deleting...' : `Delete ${selected.size}`}
            </button>
          )}
          <button
            onClick={handleExport}
            disabled={filtered.length === 0}
            title="Export CSV"
            className="p-1.5 rounded transition-colors disabled:opacity-40"
            style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-muted)' }}
          >
            <Download size={14} />
          </button>
          <DateRangeSelector value={dateFilter} onChange={setDateFilter} />
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <div className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
          {filtered.length} order{filtered.length !== 1 ? 's' : ''}
        </div>
        <div className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
          ${totalVolume.toFixed(2)} completed volume
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm" style={{ color: 'var(--dash-text-muted)' }}>/</span>
          <input
            type="text"
            placeholder="Search by order ID, external ID, or deposit address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-7 pr-3 py-2 rounded font-sans text-sm focus:outline-none transition-colors"
            style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-secondary)' }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded px-3 py-2 font-sans text-sm focus:outline-none"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-select-text)' }}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      {deleteError && (
        <div className="mb-4 px-3 py-2 rounded font-mono text-xs" style={{ border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: '#ef4444' }}>
          {deleteError}
        </div>
      )}

      {loading ? (
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>Loading orders...</p>
      ) : filtered.length === 0 ? (
        <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
          <div className="text-3xl mb-3 opacity-30">{'\u2295'}</div>
          <p className="font-sans text-sm mb-1" style={{ color: 'var(--dash-text-muted)' }}>No orders found</p>
          <p className="font-sans text-xs" style={{ color: 'var(--dash-text-faint)' }}>
            {search || statusFilter
              ? 'Try adjusting your search or filters.'
              : 'Orders will appear here once customers start making payments.'}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded overflow-x-auto" style={{ border: '1px solid var(--dash-border)' }}>
            <table className="w-full text-sm font-sans">
              <thead>
                <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                  <th className="text-left px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      aria-label="Select all on page"
                      checked={pageOrders.length > 0 && pageOrders.every((o) => selected.has(o.id))}
                      onChange={toggleSelectAllOnPage}
                      className="cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-muted)' }}>Order ID</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-muted)' }}>External ID</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium cursor-pointer select-none" style={{ color: 'var(--dash-text-muted)' }} onClick={() => handleSort('expected_amount')}>
                    Amount{sortIndicator('expected_amount')}
                  </th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-muted)' }}>Provider</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium cursor-pointer select-none" style={{ color: 'var(--dash-text-muted)' }} onClick={() => handleSort('status')}>
                    Status{sortIndicator('status')}
                  </th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium cursor-pointer select-none" style={{ color: 'var(--dash-text-muted)' }} onClick={() => handleSort('created_at')}>
                    Created{sortIndicator('created_at')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageOrders.map((order, i) => (
                  <tr
                    key={order.id}
                    onClick={() => navigate(`${basePath}/${order.id}`)}
                    className="transition-colors cursor-pointer"
                    style={{ background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)', borderBottom: '1px solid var(--dash-border-light)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)'; }}
                  >
                    <td className="px-3 py-2.5 w-8" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select order ${order.id}`}
                        checked={selected.has(order.id)}
                        onChange={() => toggleSelect(order.id)}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <Link to={`${basePath}/${order.id}`} className="font-mono text-xs transition-colors" style={{ color: 'var(--dash-text-tertiary)' }} onClick={(e) => e.stopPropagation()}>
                          {order.id.slice(0, 8)}...
                        </Link>
                        <button onClick={(e) => { e.stopPropagation(); handleCopyId(order.id); }} className="transition-colors font-mono text-[10px]" style={{ color: 'var(--dash-text-faint)' }} title="Copy full ID">
                          {copiedId === order.id ? '\u2713' : '\u2398'}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>{order.external_order_id || '--'}</td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>${order.expected_amount.toFixed(2)}</td>
                    <td className="px-4 py-2.5"><ProviderBadge provider={order.provider} paymentProvider={order.payment_provider} /></td>
                    <td className="px-4 py-2.5"><StatusBadge status={order.status as OrderStatus} /></td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>{timeAgo(order.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>Page {page + 1} of {totalPages}</span>
              <div className="flex gap-1">
                <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1.5 rounded font-mono text-xs transition-colors disabled:opacity-30" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-muted)' }}>Prev</button>
                <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="px-3 py-1.5 rounded font-mono text-xs transition-colors disabled:opacity-30" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-muted)' }}>Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
